// =============================================================================
//  Copyright : 2021 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : dashboard for state nodes
// =============================================================================
// ---Third-party imports
import React, {createElement} from 'react'
import {Table, Loader} from 'semantic-ui-react'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import log from 'uix/logging'
import {dict, values, sorted} from 'ldash'
import {useState, useSelector} from 'uix/hooks'
import {DashSegment} from 'uix/components/grid'

// ---Local Imports
import {useStateCounts} from './hooks'
import {translateState} from './'
import {StateDescription} from './components'

// ---Globals

const statusType2eventType = {
  release: 'ReleaseStateChange',
  qs: 'QSStateChange',
  wf: 'WorkflowChange',
  metadata: 'MetadataStateChange',
  zipcapsule: 'ZipCapsule',
  apipush_obvidn: 'ApiPushChange_obvidn',
  apipush_obvurn: 'ApiPushChange_obvurn',
  apipush_datacite: 'ApiPushChange_datacite',
  srusync: 'SruSync',
  pdfcutter: 'CutterStateChange',
  ocr: 'OcrStateChange',
}

const statusType2plotNames = {
  release: ['released', 'unreleased'],
  qs: ['qs_in_qa', 'qs_error'],
  metadata: ['metadata_complete'],
  zipcapsule: ['zipcapsule'],
}

const StatesCounts = ({statusType, statusName}) => {
  let i18n = useSelector(Sel.i18n)
  let availableStates = useStateCounts(statusType) || []
  let label = v => translateState(i18n, statusType, v)
  let rows = sorted(availableStates, state => label(state.value)).map(state => {
    return (
      <Table.Row key={state.name} active={statusName == state.name}>
        <Table.Cell>
          <StateDescription
            statusType={statusType}
            statusValue={state.value}
            icon={{width: 16, verticalAlign: 'middle'}}
          />
        </Table.Cell>
        <Table.Cell>{state.count}</Table.Cell>
      </Table.Row>
    )
  })

  return (
    <DashSegment header={i18n.get(`${statusType}_state_tree`)}>
      {rows.length > 0 && (
        <Table definition compact="very">
          <Table.Body>{rows}</Table.Body>
        </Table>
      )}
    </DashSegment>
  )
}

export const getStateDashboardPanels = ({statusType, statusName}) => {
  let panels = []

  let eventTypes = []
  let days = 7
  let eventFilter = null
  if (statusType) {
    if (statusType2eventType[statusType]) {
      eventTypes = [statusType2eventType[statusType]]
      days = 30
    }
    if (statusName) {
      eventFilter = e => e.name === statusName
    }
  } else {
    eventTypes = values(statusType2eventType)
  }
  if (eventTypes.length)
    panels.push({
      name: 'NodeTimeline',
      eventTypes,
      eventFilter,
      days,
    })

  panels.push({
    name: 'NodeStatesCounts',
    Comp: () => <StatesCounts statusType={statusType} statusName={statusName} />,
    align: 'right',
  })

  if (statusType && statusType2plotNames[statusType]) {
    panels.push({
      name: 'PlotPanel',
      align: 'right',
      plotName: statusType2plotNames[statusType],
    })
  }

  return panels
}

export const getAllStateDashboardPanels = vlmEnabledStates => {
  return vlmEnabledStates.map((statusType, i) => ({
    name: `NodeStatesCounts_${statusType}`,
    Comp: () => <StatesCounts statusType={statusType} statusName={null} />,
    align: i % 2 == 0 ? 'left' : 'right',
  }))
}
